import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  InboxArrowDownIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  TruckIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  usePurchaseOrdersPage,
  usePurchaseOrderSummary,
  useUpdatePurchaseOrderStatus,
  useDeletePurchaseOrder,
} from '../../hooks/usePurchase';
import { PurchaseOrder } from '../../services/purchase.service';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import {
  ConfirmDialog,
  DetailCard,
  DetailItem,
  FormActions,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import CreatePurchaseOrder from './CreatePurchaseOrder';
import { defaultRowsPerPage } from '../../lib/preferences';
import { PlantTag } from '../../components/common/PlantField';

type PurchaseStatus = PurchaseOrder['status'];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_META: Record<PurchaseStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'text-gray-700 bg-gray-50 ring-gray-500/20' },
  approved: { label: 'Approved', color: 'text-blue-700 bg-blue-50 ring-blue-600/20' },
  partially_received: { label: 'Partly received', color: 'text-amber-800 bg-amber-50 ring-amber-600/20' },
  received: { label: 'Received', color: 'text-green-700 bg-green-50 ring-green-600/20' },
  cancelled: { label: 'Cancelled', color: 'text-red-700 bg-red-50 ring-red-600/20' },
};

const FALLBACK_BADGE = 'text-gray-700 bg-gray-50 ring-gray-500/20';

const STATUS_ACTIONS: Record<
  'approved' | 'received' | 'cancelled',
  { title: string; message: string; confirmLabel: string; tone: 'primary' | 'danger'; success: string }
> = {
  approved: {
    title: 'Approve Purchase Order',
    message: 'The order will be approved and can then be received.',
    confirmLabel: 'Approve',
    tone: 'primary',
    success: 'Purchase order approved',
  },
  received: {
    title: 'Mark as Received',
    message:
      'Everything still to come on this order will be received in full and added to stock. For a part delivery or a rejection, use Receive Goods instead.',
    confirmLabel: 'Mark Received',
    tone: 'primary',
    success: 'Purchase order received and stock updated',
  },
  cancelled: {
    title: 'Cancel Purchase Order',
    message: 'The order will be cancelled. This cannot be undone.',
    confirmLabel: 'Cancel Order',
    tone: 'danger',
    success: 'Purchase order cancelled',
  },
};

const formatNumber = (value: number) => Number(value || 0).toLocaleString('en-IN');

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(amount || 0));

const formatDate = (date?: string | Date) =>
  date
    ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const isOverdue = (order: PurchaseOrder) => {
  if (order.status !== 'draft' && order.status !== 'approved' && order.status !== 'partially_received') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(order.expectedDate) < today;
};

const getErrorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || fallback;
};

const PurchaseList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PurchaseStatus | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState<PurchaseOrder | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [editOrder, setEditOrder] = useState<PurchaseOrder | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PurchaseOrder | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    order: PurchaseOrder;
    status: 'approved' | 'received' | 'cancelled';
  } | null>(null);

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: ordersPage, isLoading, isFetching, error } = usePurchaseOrdersPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
  });
  const { data: summary, isLoading: isSummaryLoading } = usePurchaseOrderSummary();
  const updateStatusMutation = useUpdatePurchaseOrderStatus();
  const deleteMutation = useDeletePurchaseOrder();

  const purchaseOrders = ordersPage?.data ?? [];
  const total = ordersPage?.total ?? 0;
  const totalPages = ordersPage?.totalPages ?? 1;

  // Step back when the current page no longer exists
  useEffect(() => {
    if (ordersPage && page > 1 && page > ordersPage.totalPages) {
      setPage(Math.max(1, ordersPage.totalPages));
    }
  }, [ordersPage, page]);

  const confirmStatusUpdate = async () => {
    if (!pendingAction) return;
    const { order, status } = pendingAction;
    try {
      await updateStatusMutation.mutateAsync({ id: order.id, status });
      toast.success(STATUS_ACTIONS[status].success);
      setPendingAction(null);
    } catch (err: any) {
      toast.error(getErrorMessage(err, 'Failed to update status'));
    }
  };

  const openView = (order: PurchaseOrder) => {
    setViewOrder(order);
    setIsViewOpen(true);
  };

  const openCreate = () => {
    setEditOrder(null);
    setIsCreateModalOpen(true);
  };

  const openEdit = (order: PurchaseOrder) => {
    setEditOrder(order);
    setIsCreateModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Purchase order deleted');
      setDeleteTarget(null);
    } catch (err: any) {
      toast.error(getErrorMessage(err, 'Failed to delete purchase order'));
    }
  };

  const hasFilters = !!(search || statusFilter);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  const stats = [
    { label: 'Total Orders', value: formatNumber(summary?.total ?? 0), icon: ClipboardDocumentListIcon, tone: 'text-indigo-600 bg-indigo-50' },
    { label: 'Pending Approval', value: formatNumber(summary?.draft ?? 0), icon: ClockIcon, tone: 'text-yellow-600 bg-yellow-50' },
    { label: 'Awaiting Receipt', value: formatNumber(summary?.approved ?? 0), icon: TruckIcon, tone: 'text-blue-600 bg-blue-50' },
    { label: 'Order Value (excl. cancelled)', value: formatCurrency(summary?.totalValue ?? 0), icon: BanknotesIcon, tone: 'text-green-600 bg-green-50' },
  ];

  const selectClass =
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
  const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const badgeClass = 'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset';
  const actionClass = 'inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold disabled:opacity-50';

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading purchase orders</p>
      </div>
    );
  }

  const action = pendingAction ? STATUS_ACTIONS[pendingAction.status] : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Purchase Orders</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage purchase orders and suppliers
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Purchase Order
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">
                {isSummaryLoading ? '—' : stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order number or vendor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as PurchaseStatus | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              {(Object.keys(STATUS_META) as PurchaseStatus[]).map((status) => (
                <option key={status} value={status}>
                  {STATUS_META[status].label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Purchase Orders Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Order Number</th>
                <th className={thClass}>Vendor</th>
                <th className={thClass}>Order Date</th>
                <th className={thClass}>Expected Date</th>
                <th className={thClass}>Items</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Amount</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-sm text-gray-500 text-center" colSpan={8}>
                    Loading purchase orders...
                  </td>
                </tr>
              ) : purchaseOrders.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={8}>
                    <ClipboardDocumentListIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No purchase orders found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? 'Try changing or clearing the search and filters.'
                        : 'Add your first purchase order to start buying from vendors.'}
                    </p>
                  </td>
                </tr>
              ) : (
                purchaseOrders.map((order) => {
                  const status = STATUS_META[order.status];
                  const overdue = isOverdue(order);
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                          {order.orderNumber}
                        </span>
                        <PlantTag plantId={order.plantId} className="mt-1" />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{order.vendor?.name || 'N/A'}</div>
                        {order.vendor?.code && <div className="text-gray-500">{order.vendor.code}</div>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {formatDate(order.orderDate)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div className={overdue ? 'font-medium text-red-600' : 'text-gray-700'}>
                          {formatDate(order.expectedDate)}
                        </div>
                        {overdue && <div className="text-xs text-red-600">Overdue</div>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {order.items?.length ?? 0}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className={`${badgeClass} ${status?.color || FALLBACK_BADGE}`}>
                          {status?.label || order.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-right font-medium text-gray-900 tabular-nums">
                        {formatCurrency(order.grandTotal)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1">
                          {order.status === 'draft' && (
                            <button
                              onClick={() => setPendingAction({ order, status: 'approved' })}
                              disabled={updateStatusMutation.isPending}
                              className={`${actionClass} bg-blue-50 text-blue-700 hover:bg-blue-100`}
                            >
                              <CheckIcon className="h-3.5 w-3.5 mr-1" />
                              Approve
                            </button>
                          )}
                          {(order.status === 'approved' || order.status === 'partially_received') && (
                            <Link
                              to={`/goods-receipts?po=${order.id}`}
                              className={`${actionClass} bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}
                              title="Enter what arrived: part deliveries, rejections, batch and heat numbers"
                            >
                              <InboxArrowDownIcon className="h-3.5 w-3.5 mr-1" />
                              Receive Goods
                            </Link>
                          )}
                          {(order.status === 'approved' || order.status === 'partially_received') && (
                            <button
                              onClick={() => setPendingAction({ order, status: 'received' })}
                              disabled={updateStatusMutation.isPending}
                              className={`${actionClass} bg-green-50 text-green-700 hover:bg-green-100`}
                            >
                              <InboxArrowDownIcon className="h-3.5 w-3.5 mr-1" />
                              Mark Received
                            </button>
                          )}
                          {(order.status === 'draft' || order.status === 'approved') && (
                            <button
                              onClick={() => setPendingAction({ order, status: 'cancelled' })}
                              disabled={updateStatusMutation.isPending}
                              className={`${actionClass} bg-red-50 text-red-700 hover:bg-red-100`}
                            >
                              <XMarkIcon className="h-3.5 w-3.5 mr-1" />
                              Cancel
                            </button>
                          )}
                          <button
                            onClick={() => openView(order)}
                            className="text-gray-600 hover:text-gray-900 p-1.5 rounded hover:bg-gray-100"
                            title="View Details"
                            aria-label="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {(order.status === 'draft' || order.status === 'approved' || order.status === 'partially_received') && (
                            <button
                              onClick={() => openEdit(order)}
                              className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded hover:bg-indigo-50"
                              title={order.status === 'draft' ? 'Edit Order' : 'Amend Order'}
                              aria-label={order.status === 'draft' ? 'Edit Order' : 'Amend Order'}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {(order.status === 'draft' || order.status === 'cancelled') && (
                            <button
                              onClick={() => setDeleteTarget(order)}
                              className="text-red-600 hover:text-red-900 p-1.5 rounded hover:bg-red-50"
                              title="Delete Order"
                              aria-label="Delete Order"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} orders
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination
              className="lg:flex-1"
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Create or edit a purchase order */}
      <CreatePurchaseOrder
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => !editOrder && setPage(1)}
        order={editOrder}
      />

      {/* Status change confirmation */}
      <ConfirmDialog
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmStatusUpdate}
        title={action?.title || ''}
        message={
          <>
            <span className="font-medium text-gray-900">{pendingAction?.order.orderNumber}</span> ·{' '}
            {pendingAction?.order.vendor?.name || 'N/A'} ·{' '}
            {formatCurrency(pendingAction?.order.grandTotal ?? 0)}
            <span className="mt-1 block">{action?.message}</span>
          </>
        }
        confirmLabel={action?.confirmLabel || 'Confirm'}
        pendingLabel="Updating..."
        isPending={updateStatusMutation.isPending}
        tone={action?.tone || 'primary'}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Purchase Order"
        message={
          <>
            <span className="font-medium text-gray-900">{deleteTarget?.orderNumber}</span> for{' '}
            {deleteTarget?.vendor?.name || 'N/A'} will be permanently deleted. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteMutation.isPending}
      />

      {/* Order details */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Purchase Order Details" size="xl">
        {viewOrder && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p>
                  <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                    {viewOrder.orderNumber}
                  </span>
                </p>
                <h4 className="mt-2 text-lg font-semibold text-gray-900">{viewOrder.vendor?.name || 'N/A'}</h4>
                {viewOrder.vendor?.code && <p className="text-sm text-gray-500">{viewOrder.vendor.code}</p>}
              </div>
              <div className="flex items-center gap-2">
                {isOverdue(viewOrder) && (
                  <span className={`${badgeClass} text-red-700 bg-red-50 ring-red-600/20`}>Overdue</span>
                )}
                <span className={`${badgeClass} ${STATUS_META[viewOrder.status]?.color || FALLBACK_BADGE}`}>
                  {STATUS_META[viewOrder.status]?.label || viewOrder.status}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <DetailCard title="Order">
                <DetailItem label="Order date">
                  {formatDate(viewOrder.orderDate)}
                  {Number(viewOrder.revision) > 0 && <span className="ml-2 text-xs font-normal text-amber-700">Amended · revision {viewOrder.revision}</span>}
                </DetailItem>
                <DetailItem label="Expected date">{formatDate(viewOrder.expectedDate)}</DetailItem>
                <DetailItem label="Items">{formatNumber(viewOrder.items?.length ?? 0)}</DetailItem>
                <DetailItem label="Units">
                  {formatNumber((viewOrder.items ?? []).reduce((sum, item) => sum + Number(item.quantity), 0))}
                </DetailItem>
              </DetailCard>

              <DetailCard title="Vendor">
                <DetailItem label="Contact">{viewOrder.vendor?.contactPerson || '—'}</DetailItem>
                <DetailItem label="Phone">
                  {viewOrder.vendor?.contactPhone || viewOrder.vendor?.phone || '—'}
                </DetailItem>
                <DetailItem label="GST number">{viewOrder.vendor?.gstNumber || '—'}</DetailItem>
                <DetailItem label="Payment terms">
                  {viewOrder.vendor?.paymentTerms ? `${viewOrder.vendor.paymentTerms} days` : '—'}
                </DetailItem>
              </DetailCard>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Product</th>
                    <th className={`${thClass} text-right`}>Qty</th>
                    <th className={`${thClass} text-right`}>Unit Price</th>
                    <th className={`${thClass} text-right`}>Tax</th>
                    <th className={`${thClass} text-right`}>Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {(viewOrder.items ?? []).length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-sm text-gray-500 text-center" colSpan={5}>
                        This order has no items
                      </td>
                    </tr>
                  ) : (
                    viewOrder.items.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{item.product?.name || 'N/A'}</div>
                          <div className="text-gray-500">{item.product?.sku || ''}</div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-700 tabular-nums">
                          {formatNumber(item.quantity)}
                          {Number(item.receivedQuantity ?? 0) > 0 && (
                            <div className="text-xs font-normal text-gray-500">{formatNumber(Number(item.receivedQuantity))} received</div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-700 tabular-nums">
                          {formatCurrency(item.unitPrice)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-700 tabular-nums">
                          {formatCurrency(item.taxAmount)}
                          <span className="text-gray-500"> ({Number(item.taxRate)}%)</span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-right font-medium text-gray-900 tabular-nums">
                          {formatCurrency(item.totalAmount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</p>
                <p className="mt-1 whitespace-pre-wrap text-gray-900">{viewOrder.notes || '—'}</p>
              </div>
              <dl className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
                <div className="flex justify-between py-1">
                  <dt className="text-gray-600">Subtotal</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(viewOrder.totalAmount)}</dd>
                </div>
                <div className="flex justify-between py-1">
                  <dt className="text-gray-600">Tax</dt>
                  <dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(viewOrder.taxAmount)}</dd>
                </div>
                <div className="mt-2 flex justify-between border-t border-gray-200 pt-3">
                  <dt className="font-semibold text-gray-900">Grand Total</dt>
                  <dd className="text-base font-semibold text-gray-900 tabular-nums">
                    {formatCurrency(viewOrder.grandTotal)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        <FormActions>
          <button type="button" onClick={() => setIsViewOpen(false)} className={secondaryButtonClass}>
            Close
          </button>
          {viewOrder?.status === 'draft' && (
            <button
              type="button"
              onClick={() => {
                setIsViewOpen(false);
                openEdit(viewOrder);
              }}
              className={primaryButtonClass}
            >
              <PencilIcon className="mr-2 h-4 w-4" />
              Edit Order
            </button>
          )}
        </FormActions>
      </Modal>
    </div>
  );
};

export default PurchaseList;
